import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  ThemeProvider,
  BaseStyles,
  TextInput,
  FormControl,
  Checkbox,
  CheckboxGroup,
  Select,
} from "@primer/react";
import { SearchIcon } from "@primer/octicons-react";
import { handleEventClick } from "./calendarTooltip";
import { requestSwatch } from "./facilityFilters";

// Swatch styles matching calendar event CSS
const EVENT_SWATCHES = {
  live: { backgroundColor: "var(--bgColor-success-emphasis)" },
  preview: { backgroundColor: "var(--bgColor-accent-emphasis)" },
  archived: { backgroundColor: "var(--bgColor-neutral-emphasis)" },
};

const EVENT_STATUSES = [
  { value: "live", label: "Live" },
  { value: "preview", label: "Preview" },
  { value: "archived", label: "Archived" },
];

function Swatch({ style }) {
  const isDashed = !style.backgroundColor;
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
        ...(isDashed
          ? { border: "2px dashed", backgroundColor: "transparent", ...style }
          : { ...style }),
      }}
    />
  );
}


function StatusCheckboxGroup({ label, options, selected, onChange, swatches }) {
  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((s) => s !== value)
      : [...selected, value];
    onChange(next);
  };

  return (
    <CheckboxGroup>
      <CheckboxGroup.Label>{label}</CheckboxGroup.Label>
      {options.map((opt) => (
        <FormControl key={opt.value}>
          <Checkbox
            checked={selected.includes(opt.value)}
            onChange={() => toggle(opt.value)}
          />
          {swatches[opt.value] && (
            <FormControl.LeadingVisual>
              <Swatch style={swatches[opt.value]} />
            </FormControl.LeadingVisual>
          )}
          <FormControl.Label>{opt.label}</FormControl.Label>
        </FormControl>
      ))}
    </CheckboxGroup>
  );
}

// GR-3516: the month view's daily capacity badge.
//
// Tints follow the same thresholds as the reviewer's approval panel: under 80%
// is neutral, 80 to 100% wants attention, over 100% is a day already past its
// limit. A date carrying a capacity exception shows its raised limit, which is
// how a deliberately bigger day is told apart from a broken one.
const CAPACITY_ATTENTION_RATIO = 0.8;

export function capacityTint(used, limit) {
  // Only a missing limit is unscored. Zero is a real limit, so a day booked
  // against it is over rather than neutral.
  if (limit == null) return "neutral";
  if (used > limit) return "danger";
  if (used / limit >= CAPACITY_ATTENTION_RATIO) return "attention";
  return "neutral";
}

export function toDateKey(date) {
  // FullCalendar is given the org's named zone with no timezone plugin, so it
  // treats the zone like UTC and hands back Dates at midnight UTC whose UTC
  // fields hold the calendar date. Read those, not the local fields: local
  // getDate() on a UTC midnight is the previous day anywhere west of
  // Greenwich, which put every badge a day early in a US browser. Same
  // convention as weekStartOf in the facility schedule.
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

function CapacityBadge({ row, isPast }) {
  const classes = [
    "capacity-badge",
    `capacity-badge--${capacityTint(row.used, row.limit)}`,
    isPast ? "capacity-badge--past" : null,
  ]
    .filter(Boolean)
    .join(" ");

  const title = row.exception
    ? `Limit for this day${row.reason ? `: ${row.reason}` : ""} — ${row.used} of ${row.limit} booked`
    : `${row.used} of ${row.limit} booked`;

  return (
    <span className={classes} title={title}>
      {row.exception && (
        <span className="capacity-badge__exception" aria-hidden="true">
          ★
        </span>
      )}
      {`${row.used}/${row.limit}`}
    </span>
  );
}

function CalendarFilters({ filters, onChange, facilities, requestStatuses }) {
  const debouncedSearch = useDebounce(
    useCallback((value) => onChange((prev) => ({ ...prev, search: value })), [onChange]),
    300
  );

  return (
    <ThemeProvider>
      <BaseStyles style={{ background: "transparent" }}>
        <div className="d-flex flex-column" style={{ gap: "16px" }}>
          <FormControl>
            <FormControl.Label>Search</FormControl.Label>
            <TextInput
              type="search"
              placeholder="Search by title..."
              defaultValue={filters.search}
              onChange={(e) => debouncedSearch(e.target.value)}
              leadingVisual={SearchIcon}
              block
              size="large"
            />
          </FormControl>

          <CheckboxGroup>
            <CheckboxGroup.Label>Type</CheckboxGroup.Label>
            <FormControl>
              <Checkbox
                checked={filters.showEvents}
                onChange={(e) => onChange((prev) => ({ ...prev, showEvents: e.target.checked }))}
              />
              <FormControl.Label>Events</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.showRequests}
                onChange={(e) => onChange((prev) => ({ ...prev, showRequests: e.target.checked }))}
              />
              <FormControl.Label>Event Requests</FormControl.Label>
            </FormControl>
          </CheckboxGroup>

          {filters.showEvents && (
            <StatusCheckboxGroup
              label="Event Status"
              options={EVENT_STATUSES}
              selected={filters.eventStatuses}
              onChange={(val) => onChange((prev) => ({ ...prev, eventStatuses: val }))}
              swatches={EVENT_SWATCHES}
            />
          )}

          {filters.showRequests && (
            <StatusCheckboxGroup
              label="Request Status"
              options={requestStatuses}
              selected={filters.requestStatuses}
              onChange={(val) => onChange((prev) => ({ ...prev, requestStatuses: val }))}
              swatches={Object.fromEntries(requestStatuses.map((s) => [s.value, requestSwatch(s.variant)]))}
            />
          )}

          {filters.showRequests && facilities.length > 0 && (
            <FormControl>
              <FormControl.Label>Facility</FormControl.Label>
              <Select
                value={filters.facilityId}
                onChange={(e) => onChange((prev) => ({ ...prev, facilityId: e.target.value }))}
                block
                size="large"
              >
                <Select.Option value="">All facilities</Select.Option>
                {facilities.map((f) => (
                  <Select.Option key={f.id} value={f.id}>
                    {f.name} ({f.type})
                  </Select.Option>
                ))}
              </Select>
            </FormControl>
          )}

          <CheckboxGroup>
            <CheckboxGroup.Label>Display</CheckboxGroup.Label>
            <FormControl>
              <Checkbox
                checked={filters.weekends}
                onChange={(e) => onChange((prev) => ({ ...prev, weekends: e.target.checked }))}
              />
              <FormControl.Label>Show Weekends</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.businessHours}
                onChange={(e) => onChange((prev) => ({ ...prev, businessHours: e.target.checked }))}
              />
              <FormControl.Label>Show Business Hours</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.showCapacity}
                onChange={(e) => onChange((prev) => ({ ...prev, showCapacity: e.target.checked }))}
              />
              <FormControl.Label>Show daily capacity</FormControl.Label>
            </FormControl>
          </CheckboxGroup>

        </div>
      </BaseStyles>
    </ThemeProvider>
  );
}

export function AdminEventsCalendar({ eventsUrl, capacityUrl, timezone, facilities = [], requestStatuses = [] }) {
  const calendarRef = useRef(null);
  const [filters, setFilters] = useState({
    showEvents: true,
    showRequests: true,
    eventStatuses: [],
    requestStatuses: [],
    facilityId: "",
    search: "",
    weekends: true,
    businessHours: true,
    showCapacity: true,
  });
  // Keyed by calendar date (see toDateKey). Only limited days are in here: the feed
  // leaves out weekends and days with no limit rather than sending a zero
  // limit a badge would render as "0/0".
  const [capacity, setCapacity] = useState({});
  const [visibleRange, setVisibleRange] = useState(null);

  const fetchEvents = useCallback(
    (fetchInfo, successCallback, failureCallback) => {
      const url = new URL(eventsUrl, window.location.origin);
      url.searchParams.set("start", fetchInfo.startStr);
      url.searchParams.set("end", fetchInfo.endStr);

      if (!filters.showEvents) url.searchParams.set("exclude_events", "true");
      if (!filters.showRequests) url.searchParams.set("exclude_requests", "true");

      if (filters.eventStatuses.length > 0) {
        filters.eventStatuses.forEach((s) => url.searchParams.append("status[]", s));
      }
      if (filters.requestStatuses.length > 0) {
        filters.requestStatuses.forEach((s) => url.searchParams.append("request_status[]", s));
      }
      if (filters.facilityId) url.searchParams.set("facility_id", filters.facilityId);
      if (filters.search) url.searchParams.set("search", filters.search);

      fetch(url, {
        headers: { Accept: "application/json" },
      })
        .then((res) => res.json())
        .then(successCallback)
        .catch(failureCallback);
    },
    [eventsUrl, filters]
  );

  // The badges are their own feed, fetched on the visible range rather than
  // per event, so toggling them off costs nothing and toggling them on does
  // not refetch the events.
  useEffect(() => {
    if (!capacityUrl || !filters.showCapacity || !visibleRange) {
      if (!filters.showCapacity) setCapacity({});
      return;
    }

    const url = new URL(capacityUrl, window.location.origin);
    url.searchParams.set("start", visibleRange.start);
    url.searchParams.set("end", visibleRange.end);

    let cancelled = false;
    fetch(url, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((rows) => {
        if (cancelled) return;
        const byDate = {};
        rows.forEach((row) => {
          byDate[row.date] = row;
        });
        setCapacity(byDate);
      })
      .catch(() => {
        if (!cancelled) setCapacity({});
      });

    return () => {
      cancelled = true;
    };
  }, [capacityUrl, filters.showCapacity, visibleRange]);

  const handleDatesSet = useCallback((info) => {
    const start = toDateKey(info.start);
    const end = toDateKey(info.end);
    setVisibleRange((prev) =>
      prev && prev.start === start && prev.end === end ? prev : { start, end }
    );
  }, []);

  // Month view only: week and day views have no comparable cell to hang a
  // day-grain figure on.
  const dayCellContent = useCallback(
    (arg) => {
      const row = capacity[toDateKey(arg.date)];
      const isMonthView = arg.view?.type === "dayGridMonth";

      if (!row || !isMonthView || !filters.showCapacity) {
        return <>{arg.dayNumberText}</>;
      }

      return (
        <>
          <CapacityBadge row={row} isPast={arg.isPast} />
          {arg.dayNumberText}
        </>
      );
    },
    [capacity, filters.showCapacity]
  );

  // eventClick handled by shared popover (calendarTooltip.js)

  const filtersPortal = document.getElementById("calendar-filters-portal");

  return (
    <div className="admin-events-calendar">
      {filtersPortal &&
        createPortal(
          <CalendarFilters
            filters={filters}
            onChange={setFilters}
            facilities={facilities}
            requestStatuses={requestStatuses}
          />,
          filtersPortal
        )}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        buttonText={{
          today: "Today",
          month: "Month",
          week: "Week",
          day: "Day",
        }}
        timeZone={timezone || "local"}
        events={fetchEvents}
        eventClick={handleEventClick}
        datesSet={handleDatesSet}
        dayCellContent={dayCellContent}
        weekends={filters.weekends}
        businessHours={filters.businessHours}
        height="auto"
        nowIndicator
        dayMaxEvents={4}
        eventDisplay="block"
      />
    </div>
  );
}
